import { useState, createContext, useContext, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import HomePage from "@/pages/HomePage";
import CorporateEventsPage from "@/pages/CorporateEventsPage";
import AboutPage from "@/pages/AboutPage";
import KVKKPage from "@/pages/KVKKPage";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import AdminPage from "@/pages/AdminPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import InfluencerFormPage from "@/pages/InfluencerFormPage";
import MasterDatabasePage from "@/pages/MasterDatabasePage";
import NotFoundPage from "@/pages/NotFoundPage";
import ScrollToTop from "@/components/ScrollToTop";
import { getLanguageFromURL, getCleanPath } from "@/utils/routes";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
export const API = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';

// Language Context
const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};

// Language-aware routing component
const LanguageRouter = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [language, setLanguage] = useState('tr');

  useEffect(() => {
    const urlLanguage = getLanguageFromURL(location.pathname);
    if (urlLanguage !== language) {
      setLanguage(urlLanguage);
    }
  }, [location.pathname, language]);

  // Update document title based on language
  useEffect(() => {
    document.title = language === 'tr' ? 'Persona Ajans' : 'Persona Agency';
  }, [language]);

  // Set up favicon once on mount
  useEffect(() => {
    const updateFavicon = () => {
      // Remove old favicon links
      const oldLinks = document.querySelectorAll('link[rel*="icon"]');
      oldLinks.forEach(link => link.remove());
      
      // Add new favicon links
      const iconSizes = ['16x16', '32x32', '64x64'];
      iconSizes.forEach(size => {
        const link = document.createElement('link');
        link.rel = 'icon';
        link.type = 'image/png';
        link.sizes = size;
        link.href = `/favicon.png?v=${Date.now()}`; // Cache buster
        document.head.appendChild(link);
      });
      
      // Add shortcut icon
      const shortcutIcon = document.createElement('link');
      shortcutIcon.rel = 'shortcut icon';
      shortcutIcon.href = `/favicon.png?v=${Date.now()}`;
      document.head.appendChild(shortcutIcon);
    };
    
    updateFavicon();
  }, []);

  const toggleLanguage = () => {
    const newLanguage = language === 'tr' ? 'en' : 'tr';
    const cleanPath = getCleanPath(location.pathname);
    
    let newPath;
    if (newLanguage === 'en') {
      newPath = cleanPath === '/' ? '/en' : `/en${cleanPath}`;
      // Convert Turkish routes to English
      newPath = newPath.replace('/en/kurumsaletkinlik', '/en/corporate-events');
      newPath = newPath.replace('/en/hakkimizda', '/en/about');
      newPath = newPath.replace('/en/kvkk', '/en/privacy-policy');
      newPath = newPath.replace('/en/influencer-kayit', '/en/influencer-registration');
    } else {
      // Convert English routes to Turkish
      if (cleanPath.startsWith('/corporate-events')) newPath = '/kurumsaletkinlik';
      else if (cleanPath.startsWith('/about')) newPath = '/hakkimizda';
      else if (cleanPath.startsWith('/privacy-policy')) newPath = '/gizlilik-politikasi';
      else if (cleanPath.startsWith('/privacy')) newPath = '/kvkk';
      else if (cleanPath.startsWith('/influencer-registration')) newPath = '/influencer-kayit';
      else if (cleanPath.startsWith('/adminboraorkun')) newPath = '/adminboraorkun';
      else newPath = '/';
    }
    
    navigate(newPath);
    setLanguage(newLanguage);
  };

  return (
    <LanguageContext.Provider value={{ language, toggleLanguage }}>
      <Routes>
        {/* 301 SEO Redirects - Eski sayfalar */}
        <Route path="/sosyalmedya" element={<Navigate to="/" replace />} />
        <Route path="/musterilerimiz" element={<Navigate to="/hakkimizda" replace />} />
        <Route path="/en/social-media" element={<Navigate to="/en" replace />} />
        <Route path="/en/customers" element={<Navigate to="/en/about" replace />} />
        {/* TR slug + EN prefix yanlis kombinasyonu: /en/gizlilik-politikasi -> dogru EN URL'e yonlendir */}
        <Route path="/en/gizlilik-politikasi" element={<Navigate to="/en/privacy-policy" replace />} />
        
        {/* Turkish Routes */}
        <Route path="/" element={<HomePage />} />
        <Route path="/kurumsaletkinlik" element={<CorporateEventsPage />} />
        <Route path="/hakkimizda" element={<AboutPage />} />
        <Route path="/kvkk" element={<KVKKPage />} />
        <Route path="/gizlilik-politikasi" element={<PrivacyPolicyPage />} />
        <Route path="/adminboraorkun" element={<AdminPage />} />
        {/* Sifre sifirlama (email link) — bilingual page (kendi icinde TR/EN toggle) */}
        <Route path="/admin-reset-password" element={<ResetPasswordPage />} />
        <Route path="/influencer-kayit" element={<InfluencerFormPage />} />
        <Route path="/master-database" element={<MasterDatabasePage />} />

        {/* English Routes */}
        <Route path="/en" element={<HomePage />} />
        <Route path="/en/corporate-events" element={<CorporateEventsPage />} />
        <Route path="/en/about" element={<AboutPage />} />
        {/* KVKK sadece TR'de — Türkiye'ye özel mevzuat, EN versiyonu yok */}
        <Route path="/en/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/en/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/en/adminboraorkun" element={<AdminPage />} />
        <Route path="/en/influencer-registration" element={<InfluencerFormPage />} />
        <Route path="/en/master-database" element={<MasterDatabasePage />} />
        
        {/* 404 - Catch all undefined routes */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </LanguageContext.Provider>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <LanguageRouter />
      </BrowserRouter>
    </div>
  );
}

export default App;
